import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiClient, useAuth } from "@/App";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import {
  User,
  Scan,
  Ruler,
  Scale,
  Target,
  Activity,
  Check,
  Sparkles,
  Camera,
  ScanLine,
  RotateCcw,
} from "lucide-react";

const Profile = () => {
  const { user } = useAuth();
  const [bodyScan, setBodyScan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [showScanForm, setShowScanForm] = useState(false);

  // 3D simulated scan dialog state
  const [show3DScan, setShow3DScan] = useState(false);
  const [scan3DStage, setScan3DStage] = useState("intake"); // intake | scanning | results
  const [scan3DProgress, setScan3DProgress] = useState(0);
  const [scan3DStatus, setScan3DStatus] = useState("");
  const [scan3DInput, setScan3DInput] = useState({
    height_cm: "",
    weight_kg: "",
    sex: "neutral",
    fitness_goal: "general fitness",
    activity_level: "moderate",
  });
  const [scan3DResult, setScan3DResult] = useState(null);
  const scan3DTimerRef = useRef(null);
  
  const [scanData, setScanData] = useState({
    height_cm: "",
    weight_kg: "",
    chest_cm: "",
    waist_cm: "",
    hips_cm: "",
    shoulder_cm: "",
    inseam_cm: "",
    neck_cm: "",
    fitness_goal: "general fitness",
    activity_level: "moderate",
  });

  useEffect(() => {
    fetchBodyScan();
  }, []);

  const fetchBodyScan = async () => {
    try {
      const response = await apiClient.get("/scan");
      setBodyScan(response.data);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setScanData({ ...scanData, [field]: value });
  };

  const submitScan = async () => {
    // Validate all required fields
    const requiredFields = ["height_cm", "weight_kg", "chest_cm", "waist_cm", "hips_cm", "shoulder_cm", "inseam_cm", "neck_cm"];
    for (const field of requiredFields) {
      if (!scanData[field] || parseFloat(scanData[field]) <= 0) {
        toast.error(`Please enter a valid ${field.replace("_", " ")}`);
        return;
      }
    }

    setScanning(true);
    try {
      // Convert string values to numbers
      const numericData = {
        ...scanData,
        height_cm: parseFloat(scanData.height_cm),
        weight_kg: parseFloat(scanData.weight_kg),
        chest_cm: parseFloat(scanData.chest_cm),
        waist_cm: parseFloat(scanData.waist_cm),
        hips_cm: parseFloat(scanData.hips_cm),
        shoulder_cm: parseFloat(scanData.shoulder_cm),
        inseam_cm: parseFloat(scanData.inseam_cm),
        neck_cm: parseFloat(scanData.neck_cm),
      };

      const response = await apiClient.post("/scan", numericData);
      toast.success(`Body scan complete! +${response.data.points_earned} points`);
      setBodyScan(response.data);
      setShowScanForm(false);
    } catch (error) {
      toast.error("Failed to save body scan");
    } finally {
      setScanning(false);
    }
  };

  // ---------- Simulated 3D Scan Flow ----------
  const open3DScan = () => {
    setScan3DInput({
      height_cm: bodyScan?.height_cm || "",
      weight_kg: bodyScan?.weight_kg || "",
      sex: bodyScan?.sex || "neutral",
      fitness_goal: bodyScan?.fitness_goal || "general fitness",
      activity_level: bodyScan?.activity_level || "moderate",
    });
    setScan3DStage("intake");
    setScan3DProgress(0);
    setScan3DResult(null);
    setShow3DScan(true);
  };

  const close3DScan = () => {
    if (scan3DTimerRef.current) clearInterval(scan3DTimerRef.current);
    setShow3DScan(false);
  };

  const start3DScanning = async () => {
    const h = parseFloat(scan3DInput.height_cm);
    const w = parseFloat(scan3DInput.weight_kg);
    if (!h || !w || h < 120 || h > 230 || w < 35 || w > 200) {
      toast.error("Enter realistic height (120–230cm) and weight (35–200kg)");
      return;
    }
    setScan3DStage("scanning");
    setScan3DProgress(0);

    const steps = [
      { at: 15, label: "Calibrating depth sensors..." },
      { at: 35, label: "Detecting body landmarks..." },
      { at: 55, label: "Mapping joints & posture..." },
      { at: 75, label: "Capturing measurements..." },
      { at: 92, label: "Generating size recommendations..." },
    ];
    let p = 0;
    scan3DTimerRef.current = setInterval(() => {
      p = Math.min(100, p + 3);
      setScan3DProgress(p);
      const current = steps.find((s) => p <= s.at);
      setScan3DStatus(current ? current.label : "Finalizing...");
      if (p >= 100) {
        clearInterval(scan3DTimerRef.current);
        callSimulateApi();
      }
    }, 110);
  };

  const callSimulateApi = async () => {
    try {
      const payload = {
        height_cm: parseFloat(scan3DInput.height_cm),
        weight_kg: parseFloat(scan3DInput.weight_kg),
        sex: scan3DInput.sex,
        fitness_goal: scan3DInput.fitness_goal,
        activity_level: scan3DInput.activity_level,
      };
      const res = await apiClient.post("/scan/simulate", payload);
      setScan3DResult(res.data);
      setScan3DStage("results");
      setBodyScan(res.data);
      if (res.data.points_earned) {
        toast.success(`3D Scan complete! +${res.data.points_earned} points`);
      } else {
        toast.success("3D Scan complete — measurements updated");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Scan failed");
      setScan3DStage("intake");
    }
  };

  const restart3DScan = () => {
    setScan3DStage("intake");
    setScan3DProgress(0);
    setScan3DResult(null);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-16 h-16 border-4 border-pink-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8" data-testid="profile-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-pink-500 rounded-xl flex items-center justify-center">
              <User className="w-6 h-6 text-white" />
            </div>
            <h1 className="font-heading font-bold text-3xl md:text-4xl">Profile</h1>
          </div>
          <p className="text-muted-foreground">
            Your personal profile and body scan data.
          </p>
        </motion.div>

        <div className="space-y-6">
          {/* User Info Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-6">
                  <Avatar className="w-20 h-20">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="text-2xl">{user?.name?.[0] || "U"}</AvatarFallback>
                  </Avatar>
                  <div>
                    <h2 className="font-heading font-bold text-2xl">{user?.name}</h2>
                    <p className="text-muted-foreground">{user?.email}</p>
                    <Badge className="mt-2 bg-pink-500/10 text-pink-600 border-pink-500/20">
                      BE Member
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Body Scan Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            {bodyScan && !showScanForm ? (
              // Show existing scan results
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Scan className="w-5 h-5 text-pink-500" />
                      Your Body Scan Profile
                    </CardTitle>
                    <CardDescription>
                      Last updated: {new Date(bodyScan.created_at).toLocaleDateString()}
                      {bodyScan.scan_method === "simulated_3d" ? " · 3D Scan" : ""}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={open3DScan} data-testid="rescan-3d-btn" className="bg-pink-500 hover:bg-pink-600">
                      <Camera className="w-4 h-4 mr-2" />
                      Re-scan (3D)
                    </Button>
                    <Button variant="outline" onClick={() => setShowScanForm(true)} data-testid="update-scan-btn">
                      Manual
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Size Recommendations */}
                  <div className="grid grid-cols-3 gap-4 mb-8">
                    <div className="p-4 bg-primary/10 rounded-xl text-center">
                      <div className="text-sm text-muted-foreground mb-1">Top Size</div>
                      <div className="font-heading font-bold text-3xl text-primary">
                        {bodyScan.recommended_top_size}
                      </div>
                    </div>
                    <div className="p-4 bg-secondary/10 rounded-xl text-center">
                      <div className="text-sm text-muted-foreground mb-1">Bottom Size</div>
                      <div className="font-heading font-bold text-3xl text-secondary">
                        {bodyScan.recommended_bottom_size}
                      </div>
                    </div>
                    <div className="p-4 bg-blue-500/10 rounded-xl text-center">
                      <div className="text-sm text-muted-foreground mb-1">Shoe Size</div>
                      <div className="font-heading font-bold text-3xl text-blue-600">
                        EU {bodyScan.recommended_shoe_size}
                      </div>
                    </div>
                  </div>

                  {/* Measurements Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { label: "Height", value: `${bodyScan.height_cm} cm`, icon: Ruler },
                      { label: "Weight", value: `${bodyScan.weight_kg} kg`, icon: Scale },
                      { label: "Chest", value: `${bodyScan.chest_cm} cm`, icon: Ruler },
                      { label: "Waist", value: `${bodyScan.waist_cm} cm`, icon: Ruler },
                      { label: "Hips", value: `${bodyScan.hips_cm} cm`, icon: Ruler },
                      { label: "Shoulder", value: `${bodyScan.shoulder_cm} cm`, icon: Ruler },
                      { label: "Inseam", value: `${bodyScan.inseam_cm} cm`, icon: Ruler },
                      { label: "Neck", value: `${bodyScan.neck_cm} cm`, icon: Ruler },
                    ].map((item, index) => (
                      <div key={index} className="p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                          <item.icon className="w-4 h-4" />
                          {item.label}
                        </div>
                        <div className="font-semibold">{item.value}</div>
                      </div>
                    ))}
                  </div>

                  {/* Goals */}
                  {bodyScan.fitness_goal && (
                    <div className="mt-6 flex gap-4">
                      <Badge variant="outline" className="flex items-center gap-2">
                        <Target className="w-4 h-4" />
                        {bodyScan.fitness_goal}
                      </Badge>
                      <Badge variant="outline" className="flex items-center gap-2">
                        <Activity className="w-4 h-4" />
                        {bodyScan.activity_level}
                      </Badge>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              // Show scan form
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Scan className="w-5 h-5 text-pink-500" />
                    {bodyScan ? "Update Body Scan" : "Complete Your Body Scan"}
                  </CardTitle>
                  <CardDescription>
                    Get personalized size recommendations and earn 100 BE Points on your first scan.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* 3D Scan promo banner */}
                  {!bodyScan && !scanning && (
                    <div
                      className="relative p-5 rounded-2xl border-2 border-pink-500/30 bg-gradient-to-br from-pink-500/10 via-orange-500/10 to-amber-500/10"
                      data-testid="scan-3d-banner"
                    >
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex-1 min-w-[200px]">
                          <Badge className="bg-pink-500 text-white mb-2">FASTEST</Badge>
                          <h3 className="font-heading font-bold text-xl mb-1">
                            BE 3D Body Scan
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            Stand in front of your camera for 10 seconds — we'll capture your
                            measurements and recommend your perfect fit automatically.
                          </p>
                        </div>
                        <Button
                          onClick={open3DScan}
                          className="bg-pink-500 hover:bg-pink-600"
                          data-testid="start-3d-scan-btn"
                        >
                          <Camera className="w-4 h-4 mr-2" />
                          Start 3D Scan
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-3">
                        Prefer to enter measurements yourself? Fill in the form below.
                      </p>
                    </div>
                  )}

                  {/* Scanning Animation */}
                  {scanning && (
                    <div className="py-8 text-center">
                      <div className="relative w-32 h-48 mx-auto mb-4 bg-muted/50 rounded-2xl overflow-hidden scan-animation">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Scan className="w-12 h-12 text-pink-500 animate-pulse" />
                        </div>
                      </div>
                      <p className="text-muted-foreground">Processing your measurements...</p>
                    </div>
                  )}

                  {!scanning && (
                    <>
                      {/* Basic Measurements */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="space-y-2">
                          <Label>Height (cm)</Label>
                          <Input
                            type="number"
                            placeholder="170"
                            value={scanData.height_cm}
                            onChange={(e) => handleInputChange("height_cm", e.target.value)}
                            data-testid="input-height"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Weight (kg)</Label>
                          <Input
                            type="number"
                            placeholder="70"
                            value={scanData.weight_kg}
                            onChange={(e) => handleInputChange("weight_kg", e.target.value)}
                            data-testid="input-weight"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Chest (cm)</Label>
                          <Input
                            type="number"
                            placeholder="96"
                            value={scanData.chest_cm}
                            onChange={(e) => handleInputChange("chest_cm", e.target.value)}
                            data-testid="input-chest"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Waist (cm)</Label>
                          <Input
                            type="number"
                            placeholder="82"
                            value={scanData.waist_cm}
                            onChange={(e) => handleInputChange("waist_cm", e.target.value)}
                            data-testid="input-waist"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Hips (cm)</Label>
                          <Input
                            type="number"
                            placeholder="95"
                            value={scanData.hips_cm}
                            onChange={(e) => handleInputChange("hips_cm", e.target.value)}
                            data-testid="input-hips"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Shoulder (cm)</Label>
                          <Input
                            type="number"
                            placeholder="45"
                            value={scanData.shoulder_cm}
                            onChange={(e) => handleInputChange("shoulder_cm", e.target.value)}
                            data-testid="input-shoulder"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Inseam (cm)</Label>
                          <Input
                            type="number"
                            placeholder="78"
                            value={scanData.inseam_cm}
                            onChange={(e) => handleInputChange("inseam_cm", e.target.value)}
                            data-testid="input-inseam"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Neck (cm)</Label>
                          <Input
                            type="number"
                            placeholder="38"
                            value={scanData.neck_cm}
                            onChange={(e) => handleInputChange("neck_cm", e.target.value)}
                            data-testid="input-neck"
                          />
                        </div>
                      </div>

                      {/* Goals */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Fitness Goal</Label>
                          <Select
                            value={scanData.fitness_goal}
                            onValueChange={(v) => handleInputChange("fitness_goal", v)}
                          >
                            <SelectTrigger data-testid="select-goal">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="general fitness">General Fitness</SelectItem>
                              <SelectItem value="lose weight">Lose Weight</SelectItem>
                              <SelectItem value="build muscle">Build Muscle</SelectItem>
                              <SelectItem value="improve endurance">Improve Endurance</SelectItem>
                              <SelectItem value="maintain">Maintain Current</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Activity Level</Label>
                          <Select
                            value={scanData.activity_level}
                            onValueChange={(v) => handleInputChange("activity_level", v)}
                          >
                            <SelectTrigger data-testid="select-activity">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="sedentary">Sedentary</SelectItem>
                              <SelectItem value="light">Lightly Active</SelectItem>
                              <SelectItem value="moderate">Moderately Active</SelectItem>
                              <SelectItem value="active">Very Active</SelectItem>
                              <SelectItem value="athlete">Athlete</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="flex gap-4">
                        {bodyScan && (
                          <Button variant="outline" onClick={() => setShowScanForm(false)}>
                            Cancel
                          </Button>
                        )}
                        <Button
                          onClick={submitScan}
                          className="flex-1 bg-pink-500 hover:bg-pink-600"
                          data-testid="submit-scan-btn"
                        >
                          <Sparkles className="w-4 h-4 mr-2" />
                          {bodyScan ? "Update Profile" : "Complete Scan"} (+100 points)
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </motion.div>

          {/* Benefits Info */}
          {!bodyScan && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card className="bg-gradient-to-br from-pink-500/10 to-primary/10">
                <CardContent className="p-6">
                  <h3 className="font-heading font-semibold text-lg mb-4">Why complete your body scan?</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      "Get perfect size recommendations",
                      "Reduce returns and exchanges",
                      "Personalized AI workout plans",
                      "Earn 100 BE Points instantly",
                    ].map((benefit, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-secondary" />
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </div>
      </div>

      {/* Simulated 3D Body Scan Dialog */}
      <Dialog open={show3DScan} onOpenChange={(o) => (o ? null : close3DScan())}>
        <DialogContent className="max-w-2xl" data-testid="scan-3d-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Camera className="w-5 h-5 text-pink-500" />
              BE 3D Body Scan
            </DialogTitle>
            <DialogDescription>
              {scan3DStage === "intake" &&
                "Quick info first — we'll do the rest. (Simulated scan — no camera permission required.)"}
              {scan3DStage === "scanning" && "Stand still in good lighting..."}
              {scan3DStage === "results" && "Your measurements & recommended sizes."}
            </DialogDescription>
          </DialogHeader>

          {scan3DStage === "intake" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Height (cm)</Label>
                  <Input
                    type="number"
                    placeholder="170"
                    value={scan3DInput.height_cm}
                    onChange={(e) =>
                      setScan3DInput({ ...scan3DInput, height_cm: e.target.value })
                    }
                    data-testid="scan-3d-height"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Weight (kg)</Label>
                  <Input
                    type="number"
                    placeholder="70"
                    value={scan3DInput.weight_kg}
                    onChange={(e) =>
                      setScan3DInput({ ...scan3DInput, weight_kg: e.target.value })
                    }
                    data-testid="scan-3d-weight"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sex / Frame</Label>
                  <Select
                    value={scan3DInput.sex}
                    onValueChange={(v) => setScan3DInput({ ...scan3DInput, sex: v })}
                  >
                    <SelectTrigger data-testid="scan-3d-sex">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="neutral">Prefer not to say</SelectItem>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Goal</Label>
                  <Select
                    value={scan3DInput.fitness_goal}
                    onValueChange={(v) =>
                      setScan3DInput({ ...scan3DInput, fitness_goal: v })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general fitness">General fitness</SelectItem>
                      <SelectItem value="weight loss">Weight loss</SelectItem>
                      <SelectItem value="muscle gain">Muscle gain</SelectItem>
                      <SelectItem value="endurance">Endurance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button
                className="w-full bg-pink-500 hover:bg-pink-600"
                onClick={start3DScanning}
                data-testid="begin-3d-scan-btn"
              >
                <ScanLine className="w-4 h-4 mr-2" />
                Begin Scan
              </Button>
            </div>
          )}

          {scan3DStage === "scanning" && (
            <div className="space-y-4" data-testid="scan-3d-animation">
              <div className="relative aspect-[3/4] max-w-xs mx-auto rounded-2xl overflow-hidden bg-gradient-to-b from-slate-900 to-slate-700 border-2 border-pink-500/50">
                {/* Grid background */}
                <div
                  className="absolute inset-0 opacity-20"
                  style={{
                    backgroundImage:
                      "linear-gradient(rgba(255,107,154,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,107,154,0.5) 1px, transparent 1px)",
                    backgroundSize: "20px 20px",
                  }}
                />
                {/* Silhouette */}
                <svg
                  viewBox="0 0 200 280"
                  className="absolute inset-0 w-full h-full text-pink-400"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="100" cy="40" r="20" />
                  <line x1="100" y1="60" x2="100" y2="160" />
                  <line x1="100" y1="80" x2="60" y2="130" />
                  <line x1="100" y1="80" x2="140" y2="130" />
                  <line x1="100" y1="160" x2="75" y2="240" />
                  <line x1="100" y1="160" x2="125" y2="240" />
                  {/* Joint dots */}
                  {[
                    [100, 60],
                    [60, 130],
                    [140, 130],
                    [100, 160],
                    [75, 240],
                    [125, 240],
                  ].map(([x, y], i) => (
                    <motion.circle
                      key={i}
                      cx={x}
                      cy={y}
                      r="4"
                      fill="#FF6B9A"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: [0, 1, 0.7] }}
                      transition={{
                        duration: 0.6,
                        delay: i * 0.1,
                        repeat: Infinity,
                        repeatType: "reverse",
                      }}
                    />
                  ))}
                </svg>
                {/* Scanning line */}
                <motion.div
                  className="absolute left-0 right-0 h-1 bg-pink-400 shadow-[0_0_12px_rgba(244,114,182,0.8)]"
                  initial={{ top: "0%" }}
                  animate={{ top: "100%" }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground" data-testid="scan-3d-status">
                    {scan3DStatus}
                  </span>
                  <span className="font-mono">{scan3DProgress}%</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-pink-500 to-orange-500"
                    animate={{ width: `${scan3DProgress}%` }}
                    transition={{ ease: "linear" }}
                  />
                </div>
              </div>
            </div>
          )}

          {scan3DStage === "results" && scan3DResult && (
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
                data-testid="scan-3d-results"
              >
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 bg-primary/10 rounded-xl text-center">
                    <div className="text-xs text-muted-foreground mb-1">Top</div>
                    <div className="font-heading font-bold text-2xl text-primary">
                      {scan3DResult.recommended_top_size}
                    </div>
                  </div>
                  <div className="p-3 bg-secondary/10 rounded-xl text-center">
                    <div className="text-xs text-muted-foreground mb-1">Bottom</div>
                    <div className="font-heading font-bold text-2xl text-secondary">
                      {scan3DResult.recommended_bottom_size}
                    </div>
                  </div>
                  <div className="p-3 bg-blue-500/10 rounded-xl text-center">
                    <div className="text-xs text-muted-foreground mb-1">Shoes</div>
                    <div className="font-heading font-bold text-2xl text-blue-600">
                      EU {scan3DResult.recommended_shoe_size}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  {[
                    ["Chest", scan3DResult.chest_cm],
                    ["Waist", scan3DResult.waist_cm],
                    ["Hips", scan3DResult.hips_cm],
                    ["Shoulder", scan3DResult.shoulder_cm],
                    ["Inseam", scan3DResult.inseam_cm],
                    ["Neck", scan3DResult.neck_cm],
                  ].map(([label, v]) => (
                    <div key={label} className="p-2 bg-muted/40 rounded-lg">
                      <div className="text-xs text-muted-foreground">{label}</div>
                      <div className="font-semibold">{v} cm</div>
                    </div>
                  ))}
                </div>
                {scan3DResult.points_earned ? (
                  <div className="flex items-center gap-2 p-3 bg-accent/10 rounded-lg">
                    <Sparkles className="w-4 h-4 text-accent" />
                    <span className="text-sm">
                      You earned{" "}
                      <span className="font-bold">+{scan3DResult.points_earned} BE Points</span>
                    </span>
                  </div>
                ) : null}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={restart3DScan}
                    className="flex-1"
                    data-testid="rescan-3d-again-btn"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Scan Again
                  </Button>
                  <Button
                    className="flex-1 bg-pink-500 hover:bg-pink-600"
                    onClick={close3DScan}
                    data-testid="scan-3d-done-btn"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Done
                  </Button>
                </div>
              </motion.div>
            </AnimatePresence>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Profile;
